/*
 * AccountPanel.jsx
 * Created by michaelbray on 10/3/17
 */

import React from 'react';
import PropTypes from 'prop-types';

import { isLoggedIn } from '../../../helpers/securityHelper';

import AccountSignIn from './AccountSignIn';
import AccountSignOut from './AccountSignOut';
import CSRSignOut from './CSRSignOut';
import CreateAccountModal from './createAccount/CreateAccountModal';
import SignInModal from './signIn/SignInModal';
import SignInGuestModal from './signInGuest/SignInGuestModal';
import ResetPasswordModal from './resetPassword/ResetPasswordModal';
import CartWarningModal from '../cart/CartWarningModal';
import SelectCustomerModal from './selectCustomer/SelectCustomerModal';

import { ACCOUNT_PROPS, CSR_ACCOUNT_PROPS, LOGIN_NOTICE_PROPS } from '../constants';

const propTypes = {
    accountServiceEnabled: PropTypes.bool,
    cart: PropTypes.shape({
        showSessionWarning: PropTypes.PropTypes.bool,
        expiration: PropTypes.string,
        showTimer: PropTypes.bool,
        reservation_count: PropTypes.number,
    }),
    account: ACCOUNT_PROPS,
    csrAccount: CSR_ACCOUNT_PROPS,
    loginNotice: LOGIN_NOTICE_PROPS,
    loginError: PropTypes.bool,
    mfaRequired: PropTypes.bool,
    lockout: PropTypes.bool,
    fetchCart: PropTypes.func,
    guestLogin: PropTypes.func,
    login: PropTypes.func,
    loggedIn: PropTypes.func,
    logout: PropTypes.func,
    csrSelectCustomer: PropTypes.func,
    showLoginModal: PropTypes.bool,
    showCreateAccountModal: PropTypes.bool,
    showSignInGuestModal: PropTypes.bool,
    showResetModal: PropTypes.bool,
    clearShowLoginModal: PropTypes.func,
    clearShowCreateAccountModal: PropTypes.func,
    clearShowSignInGuestModal: PropTypes.func,
    clearShowResetModal: PropTypes.func,
    extendTimer: PropTypes.func,
    isHero: PropTypes.bool,
    isGuest: PropTypes.bool,
    verification: PropTypes.string
};

const defaultProps = {
    accountServiceEnabled: true,
    fetchCart: () => { },
    guestLogin: () => { },
    login: () => { },
    loggedIn: () => { },
    logout: () => { },
    extendTimer: () => { },
    isHero: false
};

export default class AccountPanel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            createModalOpen: false,
            signInModalOpen: false,
            signInGuestModalOpen: false,
            selectCustomerModalOpen: false,
            resetModalOpen: false
        };

        this.toggleCreateModalOpen = this.toggleCreateModalOpen.bind(this);
        this.toggleSignInModalOpen = this.toggleSignInModalOpen.bind(this);
        this.toggleSignInGuestModalOpen = this.toggleSignInGuestModalOpen.bind(this);
        this.toggleResetModalOpen = this.toggleResetModalOpen.bind(this);
        this.openLoginModal = this.openLoginModal.bind(this);
        this.openCreateAccountModal = this.openCreateAccountModal.bind(this);
        this.openResetModal = this.openResetModal.bind(this);
        this.openSelectCustomerModal = this.openSelectCustomerModal.bind(this);
        this.closeSelectCustomerModal = this.closeSelectCustomerModal.bind(this);
    }

    componentDidMount() {
        if (isLoggedIn()) {
            this.props.fetchCart(true);
        }
        if (this.props.showLoginModal && !this.state.signInModalOpen) {
            this.openLoginModal();
        }
        if (this.props.showSignInGuestModal && !this.state.signInGuestModalOpen) {
            this.openSignInGuestModal();
        }
        if (this.props.showCreateAccountModal && !this.state.createModalOpen) {
            this.openCreateAccountModal();
        }
        if (this.props.showResetModal && !this.state.resetModalOpen) {
            this.openResetModal();
        }
    }

    componentDidUpdate(prevProps) {
        const {
            showLoginModal,
            showSignInGuestModal,
            showCreateAccountModal,
            showResetModal
        } = this.props;

        if (showLoginModal && !prevProps.showLoginModal && !this.state.signInModalOpen) {
            this.openLoginModal();
        }

        if (showSignInGuestModal && !prevProps.showSignInGuestModal && !this.state.signInGuestModalOpen) {
            this.openSignInGuestModal();
        }

        if (showCreateAccountModal && !prevProps.showCreateAccountModal && !this.state.createModalOpen) {
            this.openCreateAccountModal();
        }

        if (showResetModal && !prevProps.showResetModal && !this.state.resetModalOpen) {
            this.openResetModal();
        }
    }

    openLoginModal() {
        if (this.props.csrAccount) {
            this.setState({
                selectCustomerModalOpen: true
            }, () => {
                this.props.clearShowLoginModal();
            });
        }
        else {
            this.setState({
                signInModalOpen: true
            }, () => {
                this.props.clearShowLoginModal();
            });
        }
    }

    openSignInGuestModal() {
        this.setState({
            signInGuestModalOpen: true
        }, () => {
            this.props.clearShowSignInGuestModal();
        });
    }

    openCreateAccountModal() {
        this.setState({
            createModalOpen: true
        });
    }

    openResetModal() {
        this.setState({
            resetModalOpen: true
        });
    }

    toggleCreateModalOpen() {
        this.setState({
            createModalOpen: !this.state.createModalOpen,
            signInModalOpen: false,
            signInGuestModalOpen: false,
            resetModalOpen: false
        });
    }

    toggleSignInModalOpen() {
        this.setState({
            createModalOpen: false,
            signInModalOpen: !this.state.signInModalOpen,
            signInGuestModalOpen: false,
            resetModalOpen: false
        });
    }

    toggleSignInGuestModalOpen() {
        this.setState({
            createModalOpen: false,
            signInModalOpen: false,
            signInGuestModalOpen: !this.state.signInGuestModalOpen,
            resetModalOpen: false
        });
    }

    toggleResetModalOpen() {
        this.setState({
            createModalOpen: false,
            signInModalOpen: false,
            signInGuestModalOpen: false,
            resetModalOpen: !this.state.resetModalOpen
        });
    }

    openSelectCustomerModal() {
        this.setState({
            selectCustomerModalOpen: true
        });
    }

    closeSelectCustomerModal() {
        this.setState({
            selectCustomerModalOpen: false
        });
    }

    render() {
        let signInOrOutComponent = (
            <AccountSignIn
                toggleCreateModalOpen={this.toggleCreateModalOpen}
                toggleSignInModalOpen={this.toggleSignInModalOpen}
                toggleSignInGuestModalOpen={this.toggleSignInGuestModalOpen}
                toggleResetModalOpen={this.toggleResetModalOpen} />
        );

        if (this.props.account || this.props.isGuest) {
            signInOrOutComponent = (
                <AccountSignOut
                    account={this.props.account}
                    cart={this.props.cart}
                    isGuest={this.props.isGuest}
                    openLoginModal={this.openLoginModal}
                    logout={this.props.logout} />
            );
        }

        if (this.props.csrAccount) {
            signInOrOutComponent = (
                <CSRSignOut
                    account={this.props.account}
                    csrAccount={this.props.csrAccount}
                    cart={this.props.cart}
                    openSelectCustomerModal={this.openSelectCustomerModal}
                    logout={this.props.logout} />
            );
        }

        let warningModal = (
            <CartWarningModal
                sessionWarning={this.props.cart.showSessionWarning}
                expiration={this.props.cart.expiration}
                extendTimer={this.props.extendTimer}
                fetchCart={this.props.fetchCart} />
        );

        if (this.props.isHero || !this.props.cart.showTimer) {
            warningModal = null;
        }

        // We're using this boolean to set disabled={true/false} on form elements,
        // so let's coerce it so we can use it in that manner
        const disableAccountFeatures = !this.props.accountServiceEnabled;

        return (
            <div className="nav-account-panel">
                {signInOrOutComponent}
                <CreateAccountModal
                    disableAccountFeatures={disableAccountFeatures}
                    isOpen={this.state.createModalOpen}
                    closeModal={this.toggleCreateModalOpen}
                    signIn={this.toggleSignInModalOpen}
                    verification={this.props.verification} />
                <SignInModal
                    disableAccountFeatures={disableAccountFeatures}
                    isOpen={this.state.signInModalOpen}
                    closeModal={this.toggleSignInModalOpen}
                    create={this.toggleCreateModalOpen}
                    reset={this.toggleResetModalOpen}
                    guestLogin={this.props.guestLogin}
                    login={this.props.login}
                    account={this.props.account}
                    error={this.props.loginError}
                    lockout={this.props.lockout}
                    mfaRequired={this.props.mfaRequired}
                    loginNotice={this.props.loginNotice} />
                <SignInGuestModal
                    disableAccountFeatures={disableAccountFeatures}
                    isOpen={this.state.signInGuestModalOpen}
                    closeModal={this.toggleSignInGuestModalOpen}
                    create={this.toggleCreateModalOpen}
                    reset={this.toggleResetModalOpen}
                    guestLogin={this.props.guestLogin}
                    login={this.props.login}
                    account={this.props.account}
                    error={this.props.loginError}
                    lockout={this.props.lockout}
                    mfaRequired={this.props.mfaRequired}
                    loginNotice={this.props.loginNotice} />
                <ResetPasswordModal
                    isOpen={this.state.resetModalOpen}
                    closeModal={this.toggleResetModalOpen}
                    signIn={this.toggleSignInModalOpen}
                    signInGuest={this.toggleSignInGuestModalOpen} />
                <SelectCustomerModal
                    isOpen={this.state.selectCustomerModalOpen}
                    onRequestClose={this.closeSelectCustomerModal}
                    csrSelectCustomer={this.props.csrSelectCustomer} />
                {warningModal}
            </div>
        );
    }
}

AccountPanel.propTypes = propTypes;
AccountPanel.defaultProps = defaultProps;
